"use client"

import React, { useState, useContext } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Tooltip,
} from "@mui/material"
import { Edit, Delete, FilterList, Info, Add } from "@mui/icons-material"
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import mainContext from "../../contexts/mainContext"
import {
  getScheduledNotificationsController,
  updateScheduledNotificationController,
  deleteScheduledNotificationController,
} from "../../Controllers/Notifications"
import "./ScheduledNotifications.css"
import SendNotification from "./SendNotification"
import { __roles } from "../../constants"

const ScheduledNotifications = () => {
  const context = useContext(mainContext)
  const { setSnackBar, setProfileNavTitle } = context

  const [scheduledNotifications, setScheduledNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [editDialog, setEditDialog] = useState({ open: false, notification: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, notification: null })
  const [filterDialog, setFilterDialog] = useState(false)
  const [sendNotificationDialog, setSendNotificationDialog] = useState(false)
  const [filters, setFilters] = useState({
    status: "",
    fromDate: null,
    toDate: null,
  })

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    scheduledTime: null,
    startDate: null,
    endDate: null,
  })

  const roleOptions = [
    { value: "All", label: "All Users" },
    { value: __roles.seeker, label: "Customers" },
    { value: __roles.houseKeeper, label: "Housekeepers" },
    { value: __roles.babySitter, label: "Babysitters" },
  ]

  React.useEffect(() => {
    setProfileNavTitle("Custom Notifications")
    fetchScheduledNotifications()
  }, [setProfileNavTitle])

  const fetchScheduledNotifications = async (appliedFilters = {}) => {
    setLoading(true)
    try {
      const response = await getScheduledNotificationsController(appliedFilters)
      if (response.data.status === 200) {
        setScheduledNotifications(response.data.data || [])
      } else {
        setSnackBar({
          status: true,
          text: "Failed to fetch scheduled notifications",
          severity: "error",
        })
      }
    } catch (error) {
      setSnackBar({
        status: true,
        text: "Error fetching scheduled notifications",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (notification) => {
    setEditForm({
      title: notification.title,
      description: notification.description,
      scheduledTime: new Date(notification.scheduledTime),
      startDate: notification.startDate ? new Date(notification.startDate) : null,
      endDate: notification.endDate ? new Date(notification.endDate) : null,
    })
    setEditDialog({ open: true, notification })
  }

  const handleEditSubmit = async () => {
    if (!editForm.title.trim() || !editForm.description.trim() || !editForm.scheduledTime) {
      setSnackBar({
        status: true,
        text: "Please fill all required fields",
        severity: "error",
      })
      return
    }

    if (editForm.scheduledTime <= new Date()) {
      setSnackBar({
        status: true,
        text: "Scheduled time must be in the future",
        severity: "error",
      })
      return
    }

    try {
      const updateData = {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        scheduledTime: editForm.scheduledTime.toISOString(),
      }

      if (editForm.startDate) {
        updateData.startDate = editForm.startDate.toISOString()
      }
      if (editForm.endDate) {
        updateData.endDate = editForm.endDate.toISOString()
      }

      const response = await updateScheduledNotificationController(editDialog.notification._id, updateData)

      if (response.data.status === 200) {
        setSnackBar({
          status: true,
          text: "Scheduled notification updated successfully",
          severity: "success",
        })
        setEditDialog({ open: false, notification: null })
        fetchScheduledNotifications(filters)
      } else {
        setSnackBar({
          status: true,
          text: response.data.message || "Failed to update notification",
          severity: "error",
        })
      }
    } catch (error) {
      setSnackBar({
        status: true,
        text: "Error updating notification",
        severity: "error",
      })
    }
  }

  const handleDelete = async () => {
    try {
      const response = await deleteScheduledNotificationController(deleteDialog.notification._id)

      if (response.data.status === 200) {
        setSnackBar({
          status: true,
          text: "Scheduled notification deleted successfully",
          severity: "success",
        })
        setDeleteDialog({ open: false, notification: null })
        fetchScheduledNotifications(filters)
      } else {
        setSnackBar({
          status: true,
          text: response.data.message || "Failed to delete notification",
          severity: "error",
        })
      }
    } catch (error) {
      setSnackBar({
        status: true,
        text: "Error deleting notification",
        severity: "error",
      })
    }
  }

  const handleFilterApply = () => {
    const appliedFilters = {}
    if (filters.status) appliedFilters.status = filters.status
    if (filters.fromDate) appliedFilters.fromDate = filters.fromDate.toISOString()
    if (filters.toDate) appliedFilters.toDate = filters.toDate.toISOString()

    fetchScheduledNotifications(appliedFilters)
    setFilterDialog(false)
  }

  const handleFilterReset = () => {
    setFilters({
      status: "",
      fromDate: null,
      toDate: null,
    })
    fetchScheduledNotifications()
    setFilterDialog(false)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusClass = (status) => {
    switch (status) {
      case "Pending":
        return "status-pending"
      case "Delivered":
        return "status-delivered"
      case "Failed":
        return "status-failed"
      default:
        return "status-pending"
    }
  }

  const formatRoles = (roles) => {
    if (!roles || roles.length === 0) return []
    if (
      roles.length === 3 &&
      roles.includes(__roles.seeker) &&
      roles.includes(__roles.houseKeeper) &&
      roles.includes(__roles.babySitter)
    ) {
      return ["All Users"]
    }
    return roles.map((role) => {
      const option = roleOptions.find((opt) => opt.value === role)
      return option ? option.label : role
    })
  }

  const truncateMessage = (message, maxLength = 100) => {
    if (message.length <= maxLength) return message
    return message.substring(0, maxLength) + "..."
  }

  const handleSendNotificationSuccess = () => {
    setSendNotificationDialog(false)
    fetchScheduledNotifications(filters)
  }

  return (
    <div className="scheduled-notifications-container">
      <div className="scheduled-notifications-header">
        <h2>Custom Notifications</h2>
        <div className="header-actions">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setSendNotificationDialog(true)}
            className="send-notification-btn"
          >
            Send Custom Notification
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDialog(true)}
            className="filter-btn"
          >
            Filter
          </Button>
        </div>
      </div>

      <TableContainer component={Paper} className="notifications-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Delivery Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    Loading scheduled notifications...
                  </div>
                </TableCell>
              </TableRow>
            ) : scheduledNotifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <div className="empty-state">
                    <div className="empty-state-icon">📅</div>
                    <div className="empty-state-title">No Scheduled Notifications</div>
                    <div className="empty-state-description">
                      You haven't scheduled any notifications yet. Create one by clicking "Send Custom Notification".
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              scheduledNotifications.map((notification) => (
                <TableRow key={notification._id}>
                  <TableCell>
                    <div className="notification-title">{notification.title}</div>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={notification.description} placement="top">
                      <div className="notification-message">{truncateMessage(notification.description, 80)}</div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <div className="roles-display">
                      {formatRoles(notification.roles).map((role, index) => (
                        <span key={index} className="role-badge">
                          {role}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="scheduled-date">{formatDate(notification.scheduledTime)}</div>
                    <div className="scheduled-time">{formatTime(notification.scheduledTime)}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`status-badge ${getStatusClass(notification.status)}`}>
                      {notification.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {notification.status === "Pending" && (
                      <div className="actions-container">
                        <IconButton size="small" onClick={() => handleEdit(notification)} className="edit-btn">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, notification })}
                          className="delete-btn"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Send Notification Dialog */}
      <Dialog
        open={sendNotificationDialog}
        onClose={() => setSendNotificationDialog(false)}
        maxWidth="md"
        fullWidth
        className="send-notification-dialog"
      >
        <DialogTitle className="send-notification-dialog-title">
          Send Custom Notification
          <IconButton
            aria-label="close"
            onClick={() => setSendNotificationDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent className="send-notification-dialog-content">
          <SendNotification onSuccess={handleSendNotificationSuccess} />
        </DialogContent>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Scheduled Notifications</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Delivered">Delivered</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={filters.fromDate}
                onChange={(date) => setFilters({ ...filters, fromDate: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="To Date"
                value={filters.toDate}
                onChange={(date) => setFilters({ ...filters, toDate: date })}
                minDate={filters.fromDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterReset}>Reset</Button>
          <Button onClick={() => setFilterDialog(false)}>Cancel</Button>
          <Button onClick={handleFilterApply} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, notification: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Scheduled Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Scheduled Time"
                value={editForm.scheduledTime}
                onChange={(date) => setEditForm({ ...editForm, scheduledTime: date })}
                minDateTime={new Date()}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <span style={{ fontFamily: "semi-bold", fontSize: "14px" }}>Start Date (Optional)</span>
                  <Tooltip title="Filter users who registered on or after this date">
                    <IconButton size="small">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={editForm.startDate}
                    onChange={(date) =>
                      setEditForm({ ...editForm, startDate: date, endDate: date ? new Date() : null })
                    }
                    maxDate={new Date()}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <span style={{ fontFamily: "semi-bold", fontSize: "14px" }}>End Date (Optional)</span>
                  <Tooltip title="Filter users who registered on or before this date">
                    <IconButton size="small">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={editForm.endDate}
                    onChange={(date) => setEditForm({ ...editForm, endDate: date })}
                    minDate={editForm.startDate}
                    maxDate={new Date()}
                    disabled={!editForm.startDate}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, notification: null })}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, notification: null })}>
        <DialogTitle>Delete Scheduled Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🗑️</div>
            <div style={{ fontFamily: "regular", fontSize: "16px", color: "#666" }}>
              Are you sure you want to delete this scheduled notification?
            </div>
            <div style={{ fontFamily: "semi-bold", fontSize: "14px", color: "#333", marginTop: "8px" }}>
              "{deleteDialog.notification?.title}"
            </div>
            <div style={{ fontFamily: "regular", fontSize: "12px", color: "#999", marginTop: "16px" }}>
              This action cannot be undone.
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, notification: null })}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ScheduledNotifications
